import IFellowshipComment from '../interfaces/IFellowshipComment';
import IFellowshipPost from '../interfaces/IFellowshipPost';
import IFellowshipGroup from '../interfaces/IFellowshipGroup';
import IApiResponse from "../interfaces/IApiResponse";
import SuccessApiResponse from "../data/SuccessApiResponse";
import FailedApiResponse from "../data/FailedApiResponse";
import FirebaseConfig from '../data/FirebaseConfig';
import { initializeApp } from "firebase/app"
import { Auth, getAuth, indexedDBLocalPersistence, initializeAuth, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, User} from "firebase/auth";
import { collection, query, where, getDocs, Firestore, doc, setDoc, orderBy, limit, onSnapshot, updateDoc, QuerySnapshot, DocumentData, DocumentChange, QueryDocumentSnapshot, DocumentSnapshot, getDoc, initializeFirestore, Unsubscribe, getDocsFromServer } from "firebase/firestore";
import { Functions, getFunctions, httpsCallable } from "firebase/functions";
import { getAnalytics } from "firebase/analytics";
import IUser from '../interfaces/IUser';
import UnregisterGroup from '../data/UnregisteredGroup';
import IApiMessage from '../interfaces/IApiMessage';
import IReadingPlanRecord from '../interfaces/IReadingPlanRecord';
import IPrayer from '../interfaces/IPrayer';
import IUserData from '../interfaces/IUserData';
import PlanSyncOptions, { PlanSyncOptionsValues } from '../data/PlanSyncOptions';
import { isPlatform } from '@ionic/core';
import IFellowshipGroups from '../interfaces/IFellowshipGroups';
import IGroupMembers from '../interfaces/IGroupMembers';

export interface IFellowshipService {
    loggedIn: boolean,
    currentFirebaseUser: User | null,
    currentGroup: IFellowshipGroup | null,
    postSubscription: Unsubscribe | undefined,
    createPost: (post: IFellowshipPost) => Promise<IApiResponse<boolean | string>>,
    authenticated: (onLogin:(user: IUser) => void, onLogout?:() => void) => void,
    signIn: (email:string, pwrd:string) => Promise<IApiResponse<boolean>>,
    signOut: () => void,
    updateUser: (user: IUser, groupIds: string[]) => Promise<IApiResponse<boolean>>,
    subscribeToPosts : (activeGroup: IFellowshipGroup, postLimit: number, lastModifiedDate: Date, updateFunction:(added: Array<any>, modified: Array<any>, removed: Array<any>) => void) => void | (() => void),
    // getPosts: (startingDate: Date, postLimit: number, dateType: 'newer' | 'older') => Promise<IApiResponse<IFellowshipPost[]>>,
    getOlderPosts: (startingDate: Date, postLimit: number) => Promise<IApiResponse<IFellowshipPost[]>>,
    getNewerPosts: (startingDate: Date, postLimit: number) => Promise<IApiResponse<IFellowshipPost[]>>,
    getLastModifiedPosts: (startingDate: Date, postLimit: number) => Promise<IApiResponse<IFellowshipPost[]>>,
    getPostById: (postId: string) => Promise<IApiResponse<IFellowshipPost>>,
    getUpdatedPost: (post: IFellowshipPost) => Promise<IApiResponse<IFellowshipPost>>,
    getUserGroup: (userId: string) => Promise<IApiResponse<IFellowshipGroup>>,
    getUserGroups: (userId: string, defaultGroupId?: string) => Promise<IApiResponse<IFellowshipGroup[]>>,
    getUserGroupsInfo: (groups: IFellowshipGroup[]) => IFellowshipGroups,
    updatePost: (post: IFellowshipPost) => Promise<IApiResponse<boolean>>,
    joinGroup: (
        email: string,
        password: string,
        displayName: string,
        joinCode: string,) => Promise<IApiResponse<any>>,
    createUserAndGroup: (
        email: string, 
        password: string, 
        displayName: string, 
        groupName: string, 
        groupDescription: string,) => Promise<IApiResponse<any>>,
    updateJoinCode: (groupId: string) => Promise<IApiResponse<any>>,
    updateGroup: (updatedGroup: IFellowshipGroup, onlyUpdateCloud?: boolean) => Promise<IApiResponse<IFellowshipGroup | undefined>>,
    removeMember: (group: IFellowshipGroup, memberUserId: string) => Promise<IApiResponse<IFellowshipGroup | undefined>>,
    convertDateToMills: (doc: any) => any,
    resetPassword: (email: string) => Promise<IApiResponse<any>>,
    syncUserData: (
        readingPlans: Array<IReadingPlanRecord>, 
        prayers: Array<IPrayer>,
        planSyncOption: PlanSyncOptionsValues, 
        email?: string, 
        code?: string) => Promise<IApiResponse<{
            prayersToUpdate: Array<IPrayer>, 
            prayersToAdd: Array<IPrayer>, 
            readingPlansToUpdate: Array<IReadingPlanRecord>, 
            readingPlansToAdd: Array<IReadingPlanRecord>,
        }>>,
    deleteGroup: (groupId: string) => Promise<IApiResponse<any>>,
    deleteAccountAndMemberships: (userId: string) => Promise<IApiResponse<any>>,
    deleteAccount: (userId: string) => Promise<IApiResponse<any>>,
    noGroupReset: (userId: string) => Promise<IApiResponse<any>>,
    initCurrentUser: () => void,
}

class FellowshipService implements IFellowshipService {
    fireStoreDB: Firestore | null;
    firebaseAuth: Auth | null;
    firebaseFunctions: Functions | null;
    currentGroup: IFellowshipGroup | null;
    public postSubscription: Unsubscribe | undefined;
    public initCurrentUser: () => void;
    
    public currentFirebaseUser: User | null;
    public loggedIn: boolean;
    // subscribedComments: if we subscribe to comments from a post then we should store them in an array so we can bulk unsubscribe to them when people leave a page.
    
    constructor() {
        this.currentFirebaseUser = null;
        this.currentGroup = null;
        this.postSubscription = undefined;
        this.loggedIn = false;
        this.initCurrentUser = () => { console.log('No User Initialized'); };
        const firebaseApp = initializeApp(FirebaseConfig);
        this.firebaseAuth = isPlatform('hybrid') ? 
            initializeAuth(firebaseApp, {persistence: indexedDBLocalPersistence}) :
            this.firebaseAuth = getAuth(firebaseApp);
        // used initializedFirestore and ignoreUndefinedProperties because of errors while syncing userData
        this.fireStoreDB = initializeFirestore(firebaseApp, {ignoreUndefinedProperties: true});
        this.firebaseFunctions = getFunctions();
        const analytics = getAnalytics(firebaseApp);
    }

    authenticated = (onLogin:(user: IUser) => void, onLogout?:() => void) => {
        if(this.firebaseAuth) {
            // Note: IUser and User from firebase share property names to make this work.
            // When the app loads it will automatically trigger this and try to get the user from firebase.
            onAuthStateChanged(this.firebaseAuth, user => {
                if (user) {
                    this.currentFirebaseUser = user;
                    this.loggedIn = true;
                    const currentUser: IUser = {
                        id: user.uid,
                        username: user.email!,
                        displayName: user.displayName!,
                        email: user.email!,
                    }
                    onLogin(currentUser);
                    // This should only get run if user is logged in to firebase, but it failed to load the groups for some unknown reason.
                    this.initCurrentUser = () => {
                        console.log('Attempt to reinitialize user groups.', currentUser.id)
                        onLogin(currentUser);
                    };
                } else {
                    this.currentFirebaseUser = null;
                    this.loggedIn = false;
                    if(onLogout) { onLogout(); }
                }
            });
        } else {
            console.error("Firebase Auth Not Initialized");
            this.currentFirebaseUser = null;
            this.loggedIn = false;
            if(onLogout) { onLogout(); }
        }
    }

    signIn = (email:string, pwrd:string) => {
        if(this.firebaseAuth) {
            return signInWithEmailAndPassword(this.firebaseAuth, email, pwrd)
                .then(result => SuccessApiResponse)
                .catch(error => {
                    const Message: IApiMessage = {UserMessage: error.toString(), ServerMessage: error.toString()}
                    return {...FailedApiResponse, Message};
                });
        } else {
            console.log('Firebase Auth Failed to initialize.')
            return new Promise(resolve => resolve({...FailedApiResponse, Data: false})) as Promise<IApiResponse<boolean>>;
        }
    }

    signOut = () => {
        if(this.postSubscription) {
            this.postSubscription();
        }
        if(this.firebaseAuth) {
            signOut(this.firebaseAuth).then(result => {
                console.log('Signed Out', result);
            });
        }
    }

    // *** UPDATED *** - USE NEW MULTI-GROUP METHOD 
    createUserAndGroup = (email: string, password: string, displayName: string, groupName: string, groupDescription: string) => {
        if(this.firebaseFunctions) {
            const createUserAndGroup = httpsCallable(this.firebaseFunctions, 'createUserAndAnotherGroup');
            return createUserAndGroup({email, password, displayName, groupName, groupDescription})
                .then((result: any) => {
                    if(result.data.Success && !this.loggedIn) {
                        // when user logs in this.authenticated() will catch it and load user and group into redux
                        this.signIn(email, password).catch(err => console.log('Failed Login'));
                    } 
                    return result.data as IApiResponse<any>;
                })
                .catch((err: Error) => {
                    const Message: IApiMessage = {
                        UserMessage: err.message,
                        ServerMessage: err.name,
                    }
                    return {...FailedApiResponse, Message};
                })
        } else {
            const message: IApiMessage = {UserMessage: 'Failed to Set Up Account, Please close and restart the app.', ServerMessage: ''};
            return new Promise(resolve => resolve({...FailedApiResponse, Data: message})) as Promise<IApiResponse<IApiMessage>>;
        }
    }

    // *** UPDATED *** - USE NEW MULTI-GROUP METHOD 
    updateUser = (user: IUser, groupIds: string[]) => {
        if(this.currentFirebaseUser && this.firebaseFunctions) {
            const updateDisplayName = httpsCallable(this.firebaseFunctions, 'updateDisplayNames');
            return updateDisplayName({displayName: user.displayName, groupIds}).then(result => {
                console.log('updateUser', result);
                return result.data as Promise<IApiResponse<any>>;
            })          
            .catch((error: any) => {
                console.log('failed to update profile', error)
                return {...FailedApiResponse, Data: false}
            } );
        } else {
            return new Promise(() => ({...FailedApiResponse, Data: false})) as Promise<IApiResponse<boolean>>;
        }
    }

    resetPassword = async (email: string) => {
        if(this.firebaseAuth) {
            return await sendPasswordResetEmail(this.firebaseAuth, email)
            .then(() => {
                const successResponse = {...SuccessApiResponse};
                successResponse.Message.UserMessage = 'A password reset link has been sent to your email address';
                return successResponse;
            })
            .catch((err: Error) => {
                const failResponse = {...FailedApiResponse};
                failResponse.Message.UserMessage = 'An error occurred while attempting to reset your password';
                failResponse.Message.ServerMessage = err.message;
                return failResponse;
            }); 
        } else {
            return {...FailedApiResponse}
        }
    }

    createPost = async (post:IFellowshipPost) => {
        // remove id before storing post so that id can be created on db
        const storedPost: any = post;
        delete storedPost.id;
        if (this.fireStoreDB && this.currentGroup) {
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const group = doc(groups, this.currentGroup.id);
            const posts = collection(group, 'posts');
            const newPost = doc(posts);
            return setDoc(newPost, storedPost)
                .then(result => {
                    console.log(result);
                    const response = SuccessApiResponse;
                    return {...response, Data: newPost.id}  
                }).catch((e: Error) => {
                    console.log(e);
                    const response = {...FailedApiResponse, Data: false};
                    response.Message.UserMessage = e.message;
                    return response; 
                });
        } else {
            return new Promise(() => ({...FailedApiResponse, Data: false})) as Promise<IApiResponse<boolean>>;
        }
    } 

    subscribeToPosts = (activeGroup: IFellowshipGroup, postLimit: number, lastModifiedDate: Date, updateFunction:(added: Array<any>, modified: Array<any>, removed: Array<any>) => void) => {
        if (this.fireStoreDB) {
            if (this.postSubscription) {
                // Unsubscribe from to previous set of posts.
                this.postSubscription();
            }
            if (this.currentGroup?.id !== activeGroup?.id) {
                this.currentGroup = {...activeGroup};
            }
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const group = doc(groups, activeGroup.id);
            const posts = collection(group, 'posts'); 
            const postsQuery = query(posts, orderBy('modifiedDate', 'desc'), where('modifiedDate', '>', lastModifiedDate), limit(postLimit));
            this.postSubscription = onSnapshot(postsQuery, snapshot => {
                const {added, modified, removed} = this.convertToChangedArrays(snapshot);
                updateFunction(added, modified, removed);
            });
        } else {
            console.log('Firestore db not connected');
            this.postSubscription = () => console.log('Firestore db disconnected - no subscription to close');
        }
    }

    getPosts = (startingDate: Date, postLimit: number, dateType: 'newer' | 'older') => {
        if (this.fireStoreDB && this.currentGroup) {
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const group = doc(groups, this.currentGroup.id);
            const posts = collection(group, 'posts');
            // When checking for newer post ignore hidden, but get older hidden posts to hide what might be in user local db
            const postsQuery = dateType === 'older' ? 
                query(posts, orderBy('sortDate', 'desc'), where('sortDate', '<', startingDate), limit(postLimit)) :
                query(posts, orderBy('sortDate', 'desc'), where('hidden', '==', false), where('sortDate', '>', startingDate), limit(postLimit));
            return getDocs(postsQuery).then(result => {
                    const docs = result.docs.map(doc => {
                        return this.convertToJsObject(doc) ;
                    }) as IFellowshipPost[];
                    const response: IApiResponse<IFellowshipPost[]> = SuccessApiResponse;
                    return {...response, Data: docs }
                }).catch(e => {
                    console.log(e);
                    return {...FailedApiResponse, Data: []} 
                });
        } else {
            return new Promise(() => { return {...FailedApiResponse, Data: []}}) as Promise<IApiResponse<IFellowshipPost[]>>
        }
    }

    getOlderPosts = (startingDate: Date, postLimit: number) => {
        return this.getPosts(startingDate, postLimit, 'older');
    }

    getNewerPosts = (startingDate: Date, postLimit: number) => {
        return this.getPosts(startingDate, postLimit, 'newer');
    }

    getLastModifiedPosts = (startingDate: Date, postLimit: number) => {
        if (this.fireStoreDB && this.currentGroup) {
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const group = doc(groups, this.currentGroup.id);
            const posts = collection(group, 'posts');
            const postsQuery = 
                query(posts, orderBy('modifiedDate', 'desc'), where('hidden', '==', false), where('modifiedDate', '>', startingDate), limit(postLimit));
            return getDocs(postsQuery).then(result => {
                    const docs = result.docs.map(doc => {
                        return this.convertToJsObject(doc) ;
                    }) as IFellowshipPost[];
                    const response: IApiResponse<IFellowshipPost[]> = SuccessApiResponse;
                    return {...response, Data: docs }
                }).catch(e => {
                    console.log(e);
                    return {...FailedApiResponse, Data: []} 
                });
        } else {
            return new Promise(() => { return {...FailedApiResponse, Data: []}}) as Promise<IApiResponse<IFellowshipPost[]>>
        }
    }

    getPostById = async (postId: string) => {
        console.log(this.currentGroup)
        if (this.fireStoreDB && this.currentGroup) {
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const group = doc(groups, this.currentGroup.id);
            const posts = collection(group, 'posts');
            const post = doc(posts, postId);
            console.log('hi')
            const postResponse = await getDoc(post)
                .then(result => {
                    if (result.exists()) {
                        const cloudPost = this.convertToJsObject(result) as IFellowshipPost;
                        const response = SuccessApiResponse;
                        return {...response, Data: cloudPost}  
                    } else { throw new Error('no post exists') }
                }).catch(e => {
                    console.error(e);
                    return {...FailedApiResponse, Data: undefined} 
                });
            return postResponse;
        } else {
            // Note: Updating this while the code is running can make currentGroup undefined
            console.error("getPostId: Failed - fireStoreDB or currentGroup undefined");
            return new Promise(() => { return {...FailedApiResponse, Data: undefined}}) as Promise<IApiResponse<IFellowshipPost>>;
        }
    }

    getUpdatedPost = (post: IFellowshipPost) => {
        if (this.fireStoreDB && this.currentGroup) {
            // convert modified to a date if its a number.
            const modifiedDate = Object.prototype.toString.call(post.modifiedDate) === '[object Date]' ? post.modifiedDate : new Date(post.modifiedDate);
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const group = doc(groups, this.currentGroup.id);
            const posts = collection(group, 'posts');
            const postsQuery = 
                query(posts, orderBy('modifiedDate', 'desc'), where('id', '==', post.id), where('modifiedDate', '>', modifiedDate), limit(1));
            return getDocs(postsQuery).then(result => {
                    console.log('unsubscribed updated post', result);
                    const doc = result.docs.length ? this.convertToJsObject(result.docs[0]) as IFellowshipPost : post;
                    const response: IApiResponse<IFellowshipPost> = SuccessApiResponse;
                    return {...response, Data: doc }
                }).catch(e => {
                    console.log(e);
                    return {...FailedApiResponse, Data: post} 
                });
        } else {
            console.error("getPostId: Failed - fireStoreDB or currentGroup undefined");
            return new Promise(() => { return {...FailedApiResponse, Data: post}}) as Promise<IApiResponse<IFellowshipPost>>
        }
    }

    // getRemovedPosts = (startingDate: Date, postLimit: number) => {
    //     if (this.fireStoreDB && this.currentGroup) {
    //         const groups = collection(this.fireStoreDB, 'fellowshipGroups');
    //         const group = doc(groups, this.currentGroup.id);
    //         const posts = collection(group, 'posts');
    //         const postsQuery = 
    //             query(posts, orderBy('modifiedDate', 'desc'), where('hidden', '==', true), where('modifiedDate', '>', startingDate), limit(postLimit));
    //         return getDocs(postsQuery).then(result => {
    //                 const docs = result.docs.map(doc => {
    //                     return this.convertToJsObject(doc) ;
    //                 }) as IFellowshipPost[];
    //                 const response: IApiResponse<IFellowshipPost[]> = SuccessApiResponse;
    //                 return {...response, Data: docs }
    //             }).catch(e => {
    //                 console.log(e);
    //                 return {...FailedApiResponse, Data: []} 
    //             });
    //     } else {
    //         return new Promise(() => { return {...FailedApiResponse, Data: []}}) as Promise<IApiResponse<IFellowshipPost[]>>
    //     }
    // }

    updatePost = (post: IFellowshipPost) => {
        const updatedDoc = this.convertMillsToDate(post) as IFellowshipPost;
        updatedDoc.modifiedDate = new Date(Date.now());
        delete updatedDoc.subscribed; 
        if (this.fireStoreDB && this.currentGroup) {
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const group = doc(groups, this.currentGroup.id);
            const posts = collection(group, 'posts');
            const updatedPost = doc(posts, updatedDoc.id);
            return updateDoc(updatedPost, {...updatedDoc})
                .then(result => {
                    console.log(result);
                    const response = SuccessApiResponse;
                    return {...response, Data: true}  
                }).catch(e => {
                    console.log(e);
                    return {...FailedApiResponse, Data: false} 
                })
        } else {
            // Note: Updating this while the code is running can make currentGroup undefined
            console.error("getPostId: Failed - fireStoreDB or currentGroup undefined");
            return new Promise(() => ({...FailedApiResponse, Data: false})) as Promise<IApiResponse<boolean>>;
        }
    } 

    // returns the latest values for group.
    getGroup = async (groupId: string) => {
        if(this.fireStoreDB) {
            const fellowshipGroupsCollection = collection(this.fireStoreDB, 'fellowshipGroups');
            const group = doc(fellowshipGroupsCollection, groupId);
            const groupResponse = await getDoc(group)
                .then(result => {
                    if (result.exists()) {
                        const cloudGroup = this.convertToJsObject(result) as IFellowshipGroup;
                        const response = SuccessApiResponse;
                        return {...response, Data: cloudGroup}  
                    } else { throw new Error('no group exists') }
                }).catch(e => {
                    console.error(e);
                    return {...FailedApiResponse, Data: UnregisterGroup} 
                });
            return groupResponse;
        } else {
            return new Promise(() => { return {...FailedApiResponse, Data: UnregisterGroup}}) as Promise<IApiResponse<IFellowshipGroup>>;
        }
    }

    getUserGroup = (userId: string) => {
        if (this.fireStoreDB) {
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const postsQuery = query(groups, where("memberIds", "array-contains", userId));
            return getDocs(postsQuery)
                .then(result => {
                    console.log('user', userId);
                    if (!result.empty) {
                        const group = result.docs[0];
                        this.currentGroup = this.convertToJsObject(group) as IFellowshipGroup;
                        console.log("currentGroup", group.id)
                        const response = SuccessApiResponse;
                        return {...response, Data: this.currentGroup};
                    } else { 
                        return {...FailedApiResponse, Data: UnregisterGroup};
                    }
                }).catch(e => {
                    console.log(e);
                    return {...FailedApiResponse, Data: UnregisterGroup};
                })
        } else {
            return new Promise(() => { return {...FailedApiResponse, Data: UnregisterGroup}}) as Promise<IApiResponse<IFellowshipGroup>>;
        }
    }

    getUserGroups = (userId: string, defaultGroupId?: string) => {
        if (this.fireStoreDB) {
            const groups = collection(this.fireStoreDB, 'fellowshipGroups');
            const postsQuery = query(groups, where("memberIds", "array-contains", userId));
            return getDocsFromServer(postsQuery)
                .then(result => {
                    console.log('user', userId);
                    if (!result.empty) {
                        const groups = result.docs.map(group => {
                            return this.convertToJsObject(group) as IFellowshipGroup;
                        });
                        const defaultGroup = groups.find(group => group.id === defaultGroupId);
                        // this.currentGroup is the group that the posts are subscribed to.
                        this.currentGroup = defaultGroup ? defaultGroup : groups[0];
                        console.log("currentGroups", this.currentGroup.id, groups);
                        const response = SuccessApiResponse;
                        return {...response, Data: groups};
                    } else { 
                        const Message: IApiMessage = {
                            UserMessage: 'User is not part of a group',
                            ServerMessage: 'success-no-group',
                        }
                        return {...FailedApiResponse, Message, Data: [UnregisterGroup]};
                    }
                }).catch(e => {
                    console.log(e);
                    return {...FailedApiResponse, Data: [UnregisterGroup]};
                })
        } else {
            return new Promise(() => { return {...FailedApiResponse, Data: [UnregisterGroup]}}) as Promise<IApiResponse<IFellowshipGroup[]>>;
        }
    }

    getUserGroupsInfo = (groups: IFellowshipGroup[]) => {
        const fellowshipGroups: IFellowshipGroups = {};
        
        groups.forEach(group => {
            const groupMembers: IGroupMembers = {};
            group.memberInfo.forEach(member => (
                groupMembers[member.userId] = {displayName: member.displayName, role: member.role})
            );

            fellowshipGroups[group.id] = {
                group, 
                groupMembers,
                lastSortDate: new Date(0).getTime(),
                lastModifiedDate:  new Date(0).getTime(),
                hasNewPosts: false,
            }
        });

        return fellowshipGroups;
    }

    // *** UPDATED *** - USE NEW MULTI-GROUP METHOD 
    joinGroup = (email: string, password: string, displayName: string, joinCode: string) => {
        if(this.firebaseFunctions) {
            const joinGroup = httpsCallable(this.firebaseFunctions, 'JoinAnotherGroup');
                return joinGroup({email, password, displayName, joinCode})
                    .then((result: any) => {
                        if(result.data.Success && !this.loggedIn) {
                            // when user logs in this.authenticated() will catch it and load user and group into redux
                            this.signIn(email, password).catch(err => console.log('Failed Login'));
                        } 
                        
                        return result.data as IApiResponse<any>;
                    })
                    .catch((err: Error) => {
                        const Message: IApiMessage = {
                            UserMessage: err.message,
                            ServerMessage: err.name,
                        }
                        return {...FailedApiResponse, Message};
                    })
        } else {
            const message: IApiMessage = {UserMessage: 'Failed to Join Group: Please close and restart the app.', ServerMessage: ''};
            return new Promise(resolve => resolve({...FailedApiResponse, Data: message})) as Promise<IApiResponse<IApiMessage>>;
        }
    }

    updateGroup = async (updatedGroup: IFellowshipGroup, onlyUpdateCloud?: boolean) => {
        if(this.fireStoreDB) {
            const cloudGroup = await this.getGroup(updatedGroup.id);

            if(cloudGroup.Success) {

                let groupWithoutId = this.convertMillsToDate({...updatedGroup});
                groupWithoutId.modifiedDate = new Date(Date.now());
                delete groupWithoutId.id;
                const unmodifiedMember = {memberInfo: cloudGroup.Data?.memberInfo, memberIds: cloudGroup.Data?.memberIds};
                groupWithoutId = {...groupWithoutId, ...unmodifiedMember};
                console.log('Update Group:', updatedGroup, groupWithoutId);
                
                const groups = collection(this.fireStoreDB, 'fellowshipGroups');
                const group = doc(groups, updatedGroup.id);
                return updateDoc(group, {...groupWithoutId})
                    .then(result => { 
                        console.log('Updated Group:', result);
                        // It might not be appropriate to set this to the current group anymore
                        const groupData = {...updatedGroup, ...unmodifiedMember} as IFellowshipGroup;
                        if(!onlyUpdateCloud) {
                            this.currentGroup = {...groupData} as IFellowshipGroup;
                        }
                        const response = SuccessApiResponse;
                        return {...response, Data: groupData};
                    }).catch(e => {
                        console.log(e);
                        return {...FailedApiResponse, Data: UnregisterGroup} 
                    });
            }
        }

        return new Promise(() => { return {...FailedApiResponse, Data: UnregisterGroup}}) as Promise<IApiResponse<IFellowshipGroup>>;
    }

    // *** UPDATED *** - USE NEW MULTI-GROUP METHOD 
    removeMember = (group: IFellowshipGroup, memberUserId: string) => {
        if(this.firebaseFunctions) {
            const removeMemberFunction = httpsCallable(this.firebaseFunctions, 'removeGroupMember');
            return removeMemberFunction({groupId: group.id, memberUserId})
                .then(result => {
                    const response = result.data as IApiResponse<IFellowshipGroup>;
                    const updatedGroup = {...group, memberIds: group.memberIds.filter(id => id !== memberUserId)};
                    return {...response, Data: updatedGroup};
                })
                .catch((err: Error) => {
                    const Message: IApiMessage = {
                        UserMessage: err.message,
                        ServerMessage: err.name,
                    }
                    return {...FailedApiResponse, Message};
                });
        } else {
            const message: IApiMessage = {UserMessage: 'Failed to remove member: Please close and restart the app.', ServerMessage: ''};
            return new Promise(resolve => resolve({...FailedApiResponse, Data: message})) as Promise<IApiResponse<IApiMessage>>;
        }
    }

    updateJoinCode = (groupId: string) => {
        if(this.firebaseFunctions) {
            const joinGroup = httpsCallable(this.firebaseFunctions, 'updateJoinCode');
            return joinGroup({groupId})
                .then(result => {
                    return result.data as IApiResponse<any>;
                })
                .catch((err: Error) => {
                    const Message: IApiMessage = {
                        UserMessage: err.message,
                        ServerMessage: err.name,
                    }
                    return {...FailedApiResponse, Message};
                });
        } else {
            const message: IApiMessage = {UserMessage: 'Failed to update Join Code: Please close and restart the app.', ServerMessage: ''};
            return new Promise(resolve => resolve({...FailedApiResponse, Data: message})) as Promise<IApiResponse<IApiMessage>>;
        }
    }

    convertToChangedArrays = (snapshot: QuerySnapshot<DocumentData>) => {
        const added: Array<any> = [];
        const modified: Array<any> = [];
        const removed: Array<any> = [];

        snapshot.docChanges().forEach((change: DocumentChange<DocumentData>) => {
            const exportedDoc = this.convertToJsObject(change.doc);

            switch (change.type) {
                case 'added':
                    added.push(exportedDoc);
                    break;
                case 'modified':
                    modified.push(exportedDoc);
                    break;
                case 'removed':
                    removed.push(exportedDoc);
                    break;
                default:
                    break;
            }
        });

        return { added, modified, removed }
    }

    convertToJsObject(docData: QueryDocumentSnapshot<DocumentData>) {
        const firestoreDoc = docData.data();
        firestoreDoc.id = docData.id;
        firestoreDoc.createdDate = firestoreDoc.createdDate.toMillis();
        firestoreDoc.modifiedDate = firestoreDoc.modifiedDate.toMillis();
        firestoreDoc.sortDate = firestoreDoc.sortDate ? firestoreDoc.sortDate.toMillis(): firestoreDoc.createdDate;
        if(firestoreDoc.comments?.length) {
            firestoreDoc.comments = firestoreDoc.comments.map((comment:any) => {
                const createdDate = comment.createdDate.toMillis();
                const modifiedDate = comment.modifiedDate?.nanoseconds ? comment.modifiedDate.toMillis() : undefined;
                return {...comment, createdDate, modifiedDate};
            });
        }
        return firestoreDoc;
    }

    // This should probably replace convertToJsObject and convertDateToMills and convertMillsToDate
    convertToJsObjectWithDates(docData: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>) {
        const firestoreDoc = docData.data();
        if(firestoreDoc){
            firestoreDoc.id = docData.id;
            if(firestoreDoc.createdDate) {
                firestoreDoc.createdDate = firestoreDoc.createdDate.toDate();
            }
            if(firestoreDoc.modifiedDate) {
                firestoreDoc.modifiedDate = firestoreDoc.modifiedDate.toDate();
            }
            if(firestoreDoc.sortDate){
                firestoreDoc.sortDate = firestoreDoc.sortDate();
            }
            if(firestoreDoc.comments?.length) {
                firestoreDoc.comments = firestoreDoc.comments.map((comment:any) => {
                    const createdDate = comment.createdDate.toDate();
                    const modifiedDate = comment.modifiedDate ? comment.modifiedDate.toDate() : undefined;
                    return {...comment, createdDate, modifiedDate}
                });
            }
            if(firestoreDoc.readingPlans?.length) {
                firestoreDoc.readingPlans = firestoreDoc.readingPlans.map((plan:any) => {
                    const modifiedDate = plan.modifiedDate.toDate();
                    const startDate = plan.startDate.toDate();
                    return {...plan, modifiedDate, startDate}
                });
            }
            if(firestoreDoc.prayers?.length) {
                firestoreDoc.prayers = firestoreDoc.prayers.map((prayer:any) => {
                    const createdDate = prayer.createdDate.toDate();
                    const modifiedDate = prayer.modifiedDate.toDate();
                    if(prayer.notActiveDate) {
                        const notActiveDate = prayer.notActiveDate.toDate();
                        return {...prayer, createdDate, modifiedDate, notActiveDate};
                    } else {
                        return {...prayer, createdDate, modifiedDate};
                    }
                    
                });
            }
            return firestoreDoc;
        }

        return undefined;
    }

    convertMillsToDate(doc: any) {
        const updatedDoc = doc;
        updatedDoc.createdDate = new Date(doc.createdDate);
        updatedDoc.modifiedDate = new Date(doc.modifiedDate);
        updatedDoc.sortDate = doc.sortDate ? new Date(doc.sortDate): updatedDoc.createdDate;
        if(doc.comments?.length) {
            updatedDoc.comments = doc.comments.map((comment:IFellowshipComment) => {
                const createdDate = new Date(comment.createdDate);
                return {...comment, createdDate, }
            });  
        }
        return updatedDoc;
    }

    convertDateToMills(doc: any) {
        const updatedDoc = doc;
        updatedDoc.createdDate = Date.parse(doc.createdDate.toString());
        updatedDoc.modifiedDate = Date.parse(doc.modifiedDate.toString());
        updatedDoc.sortDate = doc.sortDate ? Date.parse(doc.sortDate.toString()): updatedDoc.createdDate;
        if(doc.comments?.length) {
            updatedDoc.comments = doc.comments.map((comment:IFellowshipComment) => {
                const createdDate = Date.parse(comment.createdDate.toString());
                return {...comment, createdDate, }
            });  
        }
        return updatedDoc;
    }

    syncUserData = async (
        readingPlans: Array<IReadingPlanRecord>, 
        prayers: Array<IPrayer>, 
        planSyncOption: PlanSyncOptionsValues,
        email: string = '', 
        code: string = '') => {

        // Build apiResponse Object for function return value
        const prayersToUpdate: Array<IPrayer> = []; 
        const prayersToAdd: Array<IPrayer> = [];
        const readingPlansToUpdate: Array<IReadingPlanRecord> = [];
        const readingPlansToAdd: Array<IReadingPlanRecord> = [];
        const Data = {
            prayersToUpdate, 
            prayersToAdd, 
            readingPlansToUpdate, 
            readingPlansToAdd,
        };
        const apiResponse = {...FailedApiResponse, Data};
            
            const nowMilliseconds = Date.now();
            const userId = this.currentFirebaseUser?.uid;
            let currentUserData: IUserData = {
                id: '',
                email,
                code,
                readingPlans,
                prayers,
                modifiedDate: new Date(Date.now()),
            }

            if (userId) {

                currentUserData.id = userId;
                currentUserData.email = this.currentFirebaseUser?.email!;

                let userDataResponse = {...FailedApiResponse, Data: currentUserData};
                if(this.fireStoreDB) {
                    const userDataCollection = collection(this.fireStoreDB, 'userData');
                    const userData = doc(userDataCollection, userId);
                    userDataResponse = await getDoc(userData)
                        .then(result => {
                            if (result.exists()) {
                                const retrievedUserData = this.convertToJsObjectWithDates(result) as IUserData;
                                const response = SuccessApiResponse;
                                return {...response, Data: retrievedUserData}  
                            } else { throw new Error('no sync data exists') }
                        }).catch(e => {
                            console.log(e);
                            return {...FailedApiResponse, Data: currentUserData} 
                        })
                }
                
                // Check to see if any thing has a matching syncId
                // Choose the code last modified date version and merge data
                let updatePrayerList: Array<IPrayer> = []; 
                let updateReadingPlanList: Array<IReadingPlanRecord> = [];

                // If cloud 'userData' record was found perform update to cloud data
                if(userDataResponse.Success){

                    // PRAYER SYNC DATA MANAGEMENT     
                    // Add in the non-synced elements and give them and syncId
                    const notSyncedPrayers = prayers.filter(prayer => !!prayer.syncId === false).map(prayer => ({...prayer, syncId: (nowMilliseconds + prayer.id)}));
                    apiResponse.Data.prayersToUpdate = [...notSyncedPrayers];

                    const updatedSyncedPrayers = userDataResponse.Data.prayers.map(remotePrayer => {
                        const localPrayer = prayers.find(localSyncedPrayer => remotePrayer.syncId === localSyncedPrayer.syncId );
                        if(localPrayer && localPrayer?.modifiedDate >= remotePrayer.modifiedDate) {
                            // The local Prayer is the most up-to-date, no need to modify
                            console.log('dont update', localPrayer.title);
                            return localPrayer;
                        } else if(localPrayer) {
                            // The remote prayer is the most up-to-date, update it locally
                            console.log('update', remotePrayer.title);
                            apiResponse.Data.prayersToUpdate.push(remotePrayer);
                            return remotePrayer
                        } else {
                            // prayer doesn't exist locally, add it locally
                            console.log('add', remotePrayer.title);
                            apiResponse.Data.prayersToAdd.push(remotePrayer);
                            return remotePrayer;
                        }
                    });

                    updatePrayerList = [...notSyncedPrayers, ...updatedSyncedPrayers];


                    // BIBLE READING PLAN SYNC DATA MANAGEMENT

                    // MANAGE LOCAL PLANS - moving them to the cloud
                    // Get synced plans and store them for back up in the cloud if them have been wiped from the cloud before
                    const previouslySyncedReadingPlans = readingPlans.filter(record => !!record.syncId === true);

                    // Get non synced plans and add a syncId and add a modified date for legacy purposes if it doesn't exist
                    readingPlans.filter(record => !!record.syncId === false)
                        .forEach(notSyncedRecord => {
                            const preSyncLocalRecord = {...notSyncedRecord, syncId: (nowMilliseconds + notSyncedRecord.id), modifiedDate: new Date(nowMilliseconds)};
                            switch(planSyncOption) {
                                // This is only for Back up and Restore never hits this...
                                case PlanSyncOptions.backup:
                                    if(preSyncLocalRecord.groupId) {
                                        const remoteGroupPlan = userDataResponse.Data.readingPlans
                                            .find(remotePlan => remotePlan.groupId === preSyncLocalRecord.groupId);
                                        const planToUpdate = {...preSyncLocalRecord, syncId: remoteGroupPlan ? remoteGroupPlan.syncId : preSyncLocalRecord.syncId};
                                        // Update Locally
                                        apiResponse.Data.readingPlansToUpdate.push(planToUpdate);
                                        // send to cloud
                                        updateReadingPlanList.push(planToUpdate); 
                                    } else {
                                        // Update Locally
                                        apiResponse.Data.readingPlansToUpdate.push(preSyncLocalRecord);
                                        // send to cloud
                                        updateReadingPlanList.push(preSyncLocalRecord); 
                                    }
                                    break;
                            }
                        });
                    
                    // MANAGE REMOTE PLANS - making them local and updating them in the cloud
                    // This updates plans that have been previous synced to the cloud
                    userDataResponse.Data.readingPlans.forEach(remoteReadingPlan => {
                        const localGroupPlan = readingPlans.find(groupPlan => remoteReadingPlan.groupId === groupPlan?.groupId);
                        const localBiblePlan = readingPlans.find(localSyncedBiblePlan => remoteReadingPlan.syncId === localSyncedBiblePlan.syncId );

                        if(remoteReadingPlan.groupId) {
                            switch(planSyncOption) {
                                case PlanSyncOptions.backup:
                                    // add local plan in the cloud if it exists
                                    const toCloudPlan = localGroupPlan ? localGroupPlan : remoteReadingPlan;
                                    toCloudPlan.syncId = toCloudPlan.syncId ? toCloudPlan.syncId : 
                                        remoteReadingPlan ? remoteReadingPlan.syncId : nowMilliseconds + toCloudPlan.id;
                                    updateReadingPlanList.push(toCloudPlan);
                                    // note: no need to update locally because it is up-to-date
                                    break;
                                case PlanSyncOptions.restore:
                                    // If a user is logged in to their group they will have a group plan
                                    if(localGroupPlan) {
                                        // only update plan if they are the same type;
                                        const planToUpdate = remoteReadingPlan.readingPlanCode === localGroupPlan.readingPlanCode ?
                                            remoteReadingPlan : localGroupPlan;
                                        // keep the local id: even though this may get marked as new
                                        planToUpdate.id = localGroupPlan.id;
                                        // the reading pace is always up-to-date locally
                                        planToUpdate.sessionsPerDay = localGroupPlan.sessionsPerDay;
                                        // the reading start date is always up-to-date locally
                                        planToUpdate.startDate = localGroupPlan.startDate;
                                        // the reading name is always up-to-date locally
                                        planToUpdate.name = localGroupPlan.name;
                                        // overwrite it locally
                                        apiResponse.Data.readingPlansToUpdate.push(planToUpdate);
                                        // keep remote plan in the cloud
                                        updateReadingPlanList.push(planToUpdate);
                                    }
                                    break;
                                case PlanSyncOptions.nothing:
                                    // nothing is to be done locally
                                    // keep remote plan in the cloud
                                    updateReadingPlanList.push(remoteReadingPlan);
                                    break;
                            }
                        }
                        // The remote plan already exists locally and the local copy is newer
                        else if(localBiblePlan && localBiblePlan?.modifiedDate && localBiblePlan?.modifiedDate >= remoteReadingPlan.modifiedDate!) {
                            switch(planSyncOption) {
                                case PlanSyncOptions.backup:
                                    // no need to modify locally because its  the most up-to-date
                                    // add local plan in the cloud
                                    updateReadingPlanList.push(localBiblePlan);
                                    break;
                                case PlanSyncOptions.restore:
                                    // local is most up-to-date, overwrite it locally
                                    apiResponse.Data.readingPlansToUpdate.push(remoteReadingPlan);
                                    // keep remote plan in the cloud
                                    updateReadingPlanList.push(remoteReadingPlan);
                                    break;
                                case PlanSyncOptions.nothing:
                                    // nothing is to be done locally
                                    // keep remote plan in the cloud
                                    updateReadingPlanList.push(remoteReadingPlan);
                                    break;
                            }
                        // The remote plan already exists locally
                        } else if(localBiblePlan) {
                            switch(planSyncOption) {
                                case PlanSyncOptions.backup:
                                    // even though local plan is not most up-to-date, send to cloud
                                    updateReadingPlanList.push(localBiblePlan);
                                    break;
                                case PlanSyncOptions.restore:
                                    // remote plan is most most up-to-date, update it locally
                                    apiResponse.Data.readingPlansToUpdate.push(remoteReadingPlan);
                                    // keep remote plan in the cloud
                                    updateReadingPlanList.push(remoteReadingPlan);
                                    break;
                                case PlanSyncOptions.nothing:
                                    // keep remote plan in the cloud
                                    updateReadingPlanList.push(remoteReadingPlan);
                                    break;
                            }
                        // the remote plan does not yet exist
                        } else {
                            switch(planSyncOption) {
                                case PlanSyncOptions.backup:
                                    // Bible Reading Plan doesn't exist locally,
                                    // keep remote plan in the cloud
                                    updateReadingPlanList.push(remoteReadingPlan);
                                    break;
                                case PlanSyncOptions.restore:
                                    // remote plan is most most up-to-date, update it locally
                                    apiResponse.Data.readingPlansToAdd.push(remoteReadingPlan);
                                    // keep remote plan in the cloud
                                    updateReadingPlanList.push(remoteReadingPlan);
                                    break;
                                case PlanSyncOptions.nothing:
                                    // nothing is to be done locally
                                    // keep remote plan in the cloud
                                    updateReadingPlanList.push(remoteReadingPlan);
                                    break;
                            }
                        }                
                    });

                    // Check for plans that were once in the cloud 
                    // but are NO LONGER in the cloud (because another device removed them) and 
                    // add them for backup if planSyncOption === PlanSyncOptions.backup
                    previouslySyncedReadingPlans.forEach(record => {
                        if(planSyncOption === PlanSyncOptions.backup) {
                            // check if these were included in the localBiblePlan above.
                            const planLoadedForBackUp = !!updateReadingPlanList.find(plan => plan.syncId === record.syncId);
                            if(!planLoadedForBackUp) {
                                updateReadingPlanList.push(record);
                                console.log('Backup Previously Synced Plan:', record);
                            }   
                        };
                    });

                    // Send UPDATED UserData to cloud
                    currentUserData = {...currentUserData, prayers: updatePrayerList, readingPlans: updateReadingPlanList};
                    console.log(currentUserData);
                    let dataSyncedResponse = FailedApiResponse;
                    if(this.fireStoreDB) {
                        const userDataCollection = collection(this.fireStoreDB, 'userData');
                        const updatedUserData = doc(userDataCollection, userId);
                        console.log('What is undefined: ', currentUserData, userId, updatedUserData, userDataCollection);
                        dataSyncedResponse = await updateDoc(updatedUserData, {...currentUserData})
                            .then(result => SuccessApiResponse)
                            .catch(e => {
                                console.error('Error Message: ', e, currentUserData, userId, updatedUserData);
                                return FailedApiResponse; 
                            })
                    }

                    apiResponse.Success = dataSyncedResponse.Success;
                
                // If cloud 'userData' record was NOT found create a new cloud data record
                } else {
                    // Send NEW UserData to cloud
                    // Add a syncId if never synced before
                    updatePrayerList = prayers.map(prayer => (prayer.syncId ? prayer : {...prayer, syncId: (nowMilliseconds + prayer.id)}));
                    apiResponse.Data.prayersToUpdate = [...updatePrayerList];
                    // Add a syncId if never synced before and add a modified date for legacy purposes if it doesn't exist
                    console.log('readingPlans:', readingPlans, readingPlans.length);
                    updateReadingPlanList = readingPlans.length ? readingPlans.map(record => {
                        const returnRecord = record.syncId ? record : {...record, syncId: (nowMilliseconds + record.id)};
                        return record.modifiedDate ? returnRecord : {...returnRecord, modifiedDate: new Date(nowMilliseconds)}
                    }) : [];
                    apiResponse.Data.readingPlansToUpdate = [...updateReadingPlanList];
                    currentUserData = {...currentUserData, prayers: updatePrayerList, readingPlans: updateReadingPlanList}

                    let dataSyncedResponse = FailedApiResponse;
                    if(this.fireStoreDB) {
                        const userDataCollection = collection(this.fireStoreDB, 'userData');
                        const userData = doc(userDataCollection, userId);
                        dataSyncedResponse = await setDoc(userData, currentUserData)
                            .then(result => SuccessApiResponse)
                            .catch(e => {
                                console.log(e);
                                return FailedApiResponse; 
                            });
                    }

                    apiResponse.Success = dataSyncedResponse.Success;
                }
                console.log('syncedToCloud', currentUserData);
            }

            // return what should synced UserData with a flag indicating if new Data was updated 
            return apiResponse;
    }        

    // *** UPDATED *** - USE NEW MULTI-GROUP METHOD
    deleteGroup = (groupId: string) => {
        if(this.firebaseFunctions) {
            const deleteGroupFunction = httpsCallable(this.firebaseFunctions, 'deleteGroupOnly');
            return deleteGroupFunction({groupId})
                .then(result => {
                    return result.data as IApiResponse<any>;
                })
                .catch((err: Error) => {
                    const Message: IApiMessage = {
                        UserMessage: err.message,
                        ServerMessage: err.name,
                    }
                    return {...FailedApiResponse, Message};
                });
        } else {
            const message: IApiMessage = { UserMessage: 'Failed to Delete Group, Please close and restart the app.', ServerMessage: '' };
            return new Promise(resolve => resolve({...FailedApiResponse, Data: message})) as Promise<IApiResponse<IApiMessage>>;
        }
    }

    deleteAccountAndMemberships = (memberUserId: string) => {
        if(this.firebaseFunctions) {
            const deleteAccountFunction = httpsCallable(this.firebaseFunctions, 'deleteAccountAndMemberships');
            console.log('DELETE USER: ', memberUserId);
            return deleteAccountFunction({memberUserId})
                .then(result => {
                    return result.data as IApiResponse<any>;
                })
                .catch((err: Error) => {
                    const Message: IApiMessage = {
                        UserMessage: err.message,
                        ServerMessage: err.name,
                    }
                    return {...FailedApiResponse, Message};
                });
        } else {
            const message: IApiMessage = { UserMessage: 'Failed to Delete User Account, Please contact tech support.', ServerMessage: '' };
            return new Promise(resolve => resolve({...FailedApiResponse, Data: message})) as Promise<IApiResponse<IApiMessage>>;
        }
    }


    deleteAccount = (userId: string) => {
        if(this.firebaseFunctions) {
            const deleteAccountFunction = httpsCallable(this.firebaseFunctions, 'deleteAccount');
            return deleteAccountFunction({userId})
                .then(result => {
                    return result.data as IApiResponse<any>;
                })
                .catch((err: Error) => {
                    const Message: IApiMessage = {
                        UserMessage: err.message,
                        ServerMessage: err.name,
                    }
                    return {...FailedApiResponse, Message};
                });
        } else {
            const message: IApiMessage = { UserMessage: 'Failed to Delete User Account, Please close and restart the app.', ServerMessage: '' };
            return new Promise(resolve => resolve({...FailedApiResponse, Data: message})) as Promise<IApiResponse<IApiMessage>>;
        }
    }

    noGroupReset = (userId: string) => {
        if(this.firebaseFunctions) {
            const noGroupResetFunction = httpsCallable(this.firebaseFunctions, 'noGroupReset');
            return noGroupResetFunction({userId})
                .then(result => {
                    return result.data as IApiResponse<any>;
                })
                .catch((err: Error) => {
                    const Message: IApiMessage = {
                        UserMessage: err.message,
                        ServerMessage: err.name,
                    }
                    return {...FailedApiResponse, Message};
                });
        } else {
            const message: IApiMessage = { UserMessage: 'Failed to Delete User Account, Please close and restart the app.', ServerMessage: '' };
            return new Promise(resolve => resolve({...FailedApiResponse, Data: message})) as Promise<IApiResponse<IApiMessage>>;
        }
    }
}

export default FellowshipService;